import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
export function About() {
  return <div className="w-full bg-club-canvas" data-id="element-298">
      {/* Page Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden" data-id="element-299">
        <div className="absolute inset-0 z-0" data-id="element-300">
          <img src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Club Heritage" className="w-full h-full object-cover" data-id="element-301" />
          <div className="absolute inset-0 bg-club-navy/50 mix-blend-multiply" data-id="element-302"></div>
        </div>
        <div className="relative z-10 text-center px-4 mt-16" data-id="element-303">
          <motion.span className="eyebrow text-club-gold mb-4 block" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-304">
            Our Legacy
          </motion.span>
          <motion.h1 className="text-5xl md:text-6xl font-serif text-white mb-6" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.6
        }} data-id="element-305">
            About The Club
          </motion.h1>
        </div>
      </section>

      {/* History Section */}
      <section className="py-24 px-4 max-w-7xl mx-auto" data-id="element-306">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-307">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-308">
            <span className="eyebrow block mb-4" data-id="element-309">Established 1999</span>
            <h2 className="text-4xl mb-6" data-id="element-310">A Legacy of Culture</h2>
            <p className="text-club-ink/70 mb-6 leading-relaxed text-lg" data-id="element-311">
              Founded in 1999 by a group of visionary leaders from the
              Telugu-speaking community, the Andhra Social & Cultural
              Association has stood as a beacon of heritage, camaraderie, and
              cultural celebration for over two decades. What began as a modest
              gathering place has evolved into one of Chennai's most prestigious
              private institutions.
            </p>
            <p className="text-club-ink/70 mb-8 leading-relaxed text-lg" data-id="element-312">
              Throughout our history, we have hosted dignitaries, artists, and
              pioneers, providing a sanctuary where Telugu tradition is honored
              and the future is shaped. Our commitment to excellence remains
              unwavering, reflected in our world-class facilities and impeccable
              service.
            </p>
          </motion.div>
          <motion.div className="relative" initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-313">
            <div className="aspect-[4/5] overflow-hidden rounded-sm relative" data-id="element-314">
              <img src="https://images.unsplash.com/photo-1578683010236-d716f9a3f461?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80" alt="Historical Club Interior" className="w-full h-full object-cover" data-id="element-315" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-4 bg-club-canvasAlt border-y border-club-border" data-id="element-316">
        <div className="max-w-7xl mx-auto text-center" data-id="element-317">
          <span className="eyebrow block mb-4" data-id="element-318">Our Principles</span>
          <h2 className="text-4xl mb-16" data-id="element-319">Core Values</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-320">
            {[{
            title: 'Tradition',
            desc: 'Honoring our rich history while thoughtfully embracing the future.'
          }, {
            title: 'Excellence',
            desc: 'Delivering unparalleled service and experiences in every aspect of club life.'
          }, {
            title: 'Community',
            desc: 'Fostering meaningful connections among our distinguished members.'
          }].map((value, index) => <motion.div key={value.title} className="bg-white p-10 border border-club-border rounded-sm" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2,
            duration: 0.6
          }} data-id="element-321">
                <h3 className="text-2xl mb-4 text-club-gold" data-id="element-322">{value.title}</h3>
                <p className="text-club-ink/70 leading-relaxed" data-id="element-323">{value.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Leadership / Committee */}
      <section className="py-24 px-4 max-w-7xl mx-auto" data-id="element-324">
        <div className="text-center mb-16" data-id="element-325">
          <span className="eyebrow block mb-4" data-id="element-326">Leadership</span>
          <h2 className="text-4xl" data-id="element-327">The Committee</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-328">
          {[{
          name: 'Arthur Pendelton',
          role: 'President'
        }, {
          name: 'Eleanor Vance',
          role: 'Vice President'
        }, {
          name: 'James Sterling',
          role: 'Treasurer'
        }, {
          name: 'Victoria Chen',
          role: 'Secretary'
        }].map((member, index) => <motion.div key={member.name} className="text-center" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.5
        }} data-id="element-329">
              <div className="aspect-square bg-club-canvasAlt border border-club-border rounded-sm mb-4 flex items-center justify-center" data-id="element-330">
                <span className="text-club-ink/20 font-serif text-4xl" data-id="element-331">
                  {member.name.charAt(0)}
                </span>
              </div>
              <h4 className="text-xl mb-1" data-id="element-332">{member.name}</h4>
              <p className="text-sm text-club-gold uppercase tracking-wider" data-id="element-333">
                {member.role}
              </p>
            </motion.div>)}
        </div>
      </section>
    </div>;
}